import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  listPromptsAction,
  listLlms,
  listPromptScreens,
} from '../../../../../store/actions/promptsv2.action';
import { Dropdown, Menu } from 'antd';
import { PlusIcon, ActionMenuIcon, EditAppointmentIcon } from 'src/icons';
import { Button } from '../../../../baseComponents';
import { SuperAdminTable } from '../../../../baseComponents/Table';
import ServiceRequestsPromptsFilters from './ServiceRequestsPromptsFilters';
import EditPromptModal from './EditPromptModal';
import CreatePromptModal from './CreatePromptModal';
import organizationEventEmitter from '../../../../../../../utilities/organizationEmmiter';
import { getFromStorage } from '../../../../../../../lib/storage';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import { getLlmDisplayComponent } from 'src/utils/llmUtils';

const ServiceRequestsPrompts = () => {
  const dispatch = useDispatch();
  const {
    prompts,
    promptsLoading,
    llms,
    llmsLoading,
    promptScreens,
    promptScreensLoading,
  } = useSelector((state) => state?.promptsState);
  const { currentPractitioner, serviceRequestTypes, activeSubTab } =
    useSelector((state) => ({
      currentPractitioner: state?.practitionerState?.currentPractitioner,
      serviceRequestTypes: state?.serviceRequestState?.srTypeList,
      activeSubTab: state?.superadminState?.activeSubTab,
    }));

  // Initialize filters with organization support and service request focus
  const [filters, setFilters] = useState({
    page: 1,
    per_page: 20,
    // Filter to show only service request-based prompts
    has_service_request_type: true,
  });

  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [editingPromptId, setEditingPromptId] = useState(null);
  const [selectedOrganizationUUID, setSelectedOrganizationUUID] = useState(
    () => {
      const savedOrg = getFromStorage('superAdminSelectedOrganization');
      const parsedSavedOrg = savedOrg ? JSON.parse(savedOrg) : null;
      return parsedSavedOrg?.org_uuid || currentPractitioner?.org_uuid || null;
    }
  );

  useEffect(() => {
    if (activeSubTab === 'Service Requests') {
      dispatch(listLlms());
      dispatch(listPromptScreens(null, null, 'service_request'));
    }
  }, [dispatch, activeSubTab]);

  useEffect(() => {
    // Only make API call when this tab is active
    if (activeSubTab === 'Service Requests') {
      dispatch(
        listPromptsAction(
          { ...filters, scope: 'service_request' },
          selectedOrganizationUUID
        )
      );
    }
  }, [dispatch, filters, activeSubTab, selectedOrganizationUUID]);

  useEffect(() => {
    if (selectedOrganizationUUID) {
      dispatch(getAllActiveOrganizationAction());
    }
  }, [dispatch, selectedOrganizationUUID]);

  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      (newOrg) => {
        setSelectedOrganizationUUID(newOrg?.org_uuid);
      }
    );
    return () => unsubscribe();
  }, []);

  const handleEdit = (promptId) => {
    setIsEditModalVisible(true);
    setEditingPromptId(promptId);
  };

  const menuOptions = (id) => {
    return (
      <Menu
        onClick={({ key }) => {
          if (key === 'editPrompt') {
            handleEdit(id);
          }
        }}
      >
        <Menu.Item key="editPrompt">
          <div className="dropdown-text flex items-center space-x-2">
            <EditAppointmentIcon /> <span>Edit Prompt</span>
          </div>
        </Menu.Item>
      </Menu>
    );
  };

  const columns = [
    {
      title: <span className="font-[700]">Prompt</span>,
      dataIndex: 'prompt',
      key: 'prompt',
      render: (text) =>
        text && text.length > 50 ? `${text.substring(0, 50)}...` : text,
    },
    {
      title: <span className="font-[700]">Version</span>,
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: <span className="font-[700]">LLM</span>,
      dataIndex: 'llm_id',
      key: 'llm_id',
      render: (llm_id, record) => {
        const llmObj = llms?.llms?.find((llm) => llm.llm_id === llm_id);

        if (!llmObj) {
          return llm_id;
        }
        return getLlmDisplayComponent(llmObj);
      },
    },
    {
      title: <span className="font-[700]">Service Request Type</span>,
      key: 'service_request_type',
      render: (text, record) => {
        return serviceRequestTypes?.find(
          (srType) =>
            srType?.service_request_type_id === record?.service_request_type_id
        )?.service_request_type;
      },
    },
    {
      title: <span className="font-[700]">Prompt Screen</span>,
      dataIndex: 'prompt_screens_name',
      key: 'prompt_screens_name',
    },
    {
      title: 'Actions',
      key: 'actions',
      dataIndex: 'actions',
      render: (text, record) => {
        return (
          <Dropdown className="" overlay={menuOptions(record?.prompt_id)}>
            <a className="" onClick={(e) => e.preventDefault()}>
              <ActionMenuIcon />
            </a>
          </Dropdown>
        );
      },
    },
  ];

  const handleTableChange = (pagination) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: pagination.current,
      per_page: pagination.pageSize,
    }));
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row lg:justify-between justify-center items-center mb-4 gap-4">
        <div className="flex flex-col sm:flex-row flex-1 min-w-0">
          <ServiceRequestsPromptsFilters
            filters={filters}
            setFilters={setFilters}
          />
        </div>
        <div className="flex justify-end items-end">
          <Button type={'button'} onClick={() => setIsCreateModalVisible(true)}>
            <PlusIcon className={`mr-2`} /> Add
          </Button>
        </div>
      </div>

      <div className="mt-5 ant-table rounded-xl shadow-xl">
        <SuperAdminTable
          className={'appointment-page-table'}
          data={prompts?.prompts}
          columns={columns}
          loading={promptsLoading}
          rowKey="prompt_id"
          pagination={{
            total: prompts?.total_items,
            pageSize: prompts?.per_page,
            current: prompts?.page,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50'],
          }}
          onChange={handleTableChange}
        />
      </div>

      <EditPromptModal
        visible={isEditModalVisible}
        setVisible={setIsEditModalVisible}
        promptId={editingPromptId}
        filters={filters}
        selectedOrganizationUUID={selectedOrganizationUUID}
        scope="service_request"
      />
      <CreatePromptModal
        visible={isCreateModalVisible}
        setVisible={setIsCreateModalVisible}
        filters={filters}
        selectedOrganizationUUID={selectedOrganizationUUID}
        scope="service_request"
      />
    </div>
  );
};

export default ServiceRequestsPrompts;
